import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NotebookPen, Plus, Search, Building2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui';
import { QuickCaptureModal, NoteList } from '../components/notebook';
import { cn } from '../lib/utils';
import { getTenants, getTenantNotes } from '../services';
import { useUser } from '../context';
import type { Tenant, TenantNote } from '../data/types';

/**
 * Customer Notebook — a per-customer running journal of analyst activity across
 * the month, so analysts stop keeping a parallel notepad. Left: customer list.
 * Right: the selected customer's notes grouped by month.
 */
export function Notebook() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useUser();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [notes, setNotes] = useState<TenantNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  const [showCapture, setShowCapture] = useState(false);

  useEffect(() => {
    getTenants().then((r) => {
      if (r.success) setTenants(r.data);
      setTenantsLoading(false);
    });
  }, []);

  const loadNotes = useCallback(async (id: string) => {
    setNotesLoading(true);
    setNotesError(null);
    const r = await getTenantNotes(id);
    if (r.success) setNotes(r.data);
    else setNotesError(r.error || 'Failed to load notes');
    setNotesLoading(false);
  }, []);

  useEffect(() => {
    if (tenantId) loadNotes(tenantId);
    else setNotes([]);
  }, [tenantId, loadNotes]);

  const selectedTenant = useMemo(
    () => tenants.find((t) => t.tenantId === tenantId) ?? null,
    [tenants, tenantId]
  );

  const isElevated = currentUser?.role === 'admin' || currentUser?.role === 'senior_analyst';
  const canModifyNote = useCallback(
    (note: TenantNote) =>
      isElevated ||
      (!!currentUser &&
        !!note.authorUserId &&
        note.authorUserId.toLowerCase() === currentUser.userId.toLowerCase()),
    [isElevated, currentUser]
  );

  const filteredTenants = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? tenants.filter(
          (t) =>
            t.displayName.toLowerCase().includes(q) || t.tenantId.toLowerCase().includes(q)
        )
      : tenants;
  }, [tenants, search]);

  const handleSaved = (note: TenantNote) => {
    if (note.tenantId === tenantId) setNotes((prev) => [note, ...prev]);
  };
  const handleUpdated = (note: TenantNote) =>
    setNotes((prev) => prev.map((n) => (n.noteId === note.noteId ? note : n)));
  const handleDeleted = (noteId: string) =>
    setNotes((prev) => prev.filter((n) => n.noteId !== noteId));

  return (
    <div className="p-4 md:p-6">
      {/* Page header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-lime/15 flex items-center justify-center">
          <NotebookPen className="w-5 h-5 text-brand-navy dark:text-brand-lime" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-text-primary">Customer Notebook</h1>
          <p className="text-sm text-text-secondary">
            Running notes per customer through the month — feeds the monthly report.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Left: customer list */}
        <aside className="lg:sticky lg:top-4 lg:self-start">
          <div className="rounded-xl border border-border-default bg-bg-secondary overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border-default">
              <Search className="w-4 h-4 text-text-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search customers…"
                className="flex-1 bg-transparent text-sm text-text-primary placeholder-text-muted focus:outline-none"
              />
            </div>
            <div className="max-h-[60vh] overflow-y-auto py-1">
              {tenantsLoading ? (
                <div className="px-3 py-6 flex justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
                </div>
              ) : filteredTenants.length === 0 ? (
                <div className="px-3 py-6 text-sm text-text-muted text-center">No customers</div>
              ) : (
                filteredTenants.map((t) => {
                  const active = t.tenantId === tenantId;
                  return (
                    <button
                      key={t.tenantId}
                      type="button"
                      onClick={() => navigate(`/notebook/${t.tenantId}`)}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                        active
                          ? 'bg-brand-lime/15 text-text-primary'
                          : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                      )}
                    >
                      <Building2 className="w-4 h-4 flex-shrink-0 text-text-muted" />
                      <span className="truncate">{t.displayName}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </aside>

        {/* Right: notes */}
        <section className="min-w-0">
          {!tenantId ? (
            <div className="rounded-xl border border-dashed border-border-default bg-bg-secondary/50 p-10 text-center">
              <NotebookPen className="w-8 h-8 mx-auto mb-3 text-text-muted" />
              <p className="text-sm text-text-secondary">
                Select a customer to view and add notes.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-text-primary truncate">
                    {selectedTenant?.displayName || tenantId}
                  </h2>
                  <p className="text-xs text-text-muted">{tenantId}</p>
                </div>
                <Button variant="primary" size="sm" onClick={() => setShowCapture(true)}>
                  <Plus className="w-4 h-4" />
                  Add note
                </Button>
              </div>

              {notesLoading ? (
                <div className="py-16 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
                </div>
              ) : notesError ? (
                <div className="rounded-xl border border-border-default bg-bg-secondary p-6 text-sm text-red-600 dark:text-red-400">
                  {notesError}
                </div>
              ) : notes.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border-default bg-bg-secondary/50 p-10 text-center">
                  <p className="text-sm text-text-secondary mb-3">
                    No notes yet for this customer.
                  </p>
                  <Button variant="secondary" size="sm" onClick={() => setShowCapture(true)}>
                    <Plus className="w-4 h-4" />
                    Add the first note
                  </Button>
                </div>
              ) : (
                <NoteList
                  notes={notes}
                  canModifyNote={canModifyNote}
                  onUpdated={handleUpdated}
                  onDeleted={handleDeleted}
                />
              )}
            </>
          )}
        </section>
      </div>

      {tenantId && (
        <QuickCaptureModal
          isOpen={showCapture}
          onClose={() => setShowCapture(false)}
          tenantId={tenantId}
          tenantName={selectedTenant?.displayName}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
